// ActivityDrawer — one component, used in workspace mode for MR 5.
//
// Modes (handoff § activity drawer):
//   - workspace: full state transitions + notes + action footer
//   - preview:   read-only + "Start this framework" footer
//   - read-only: completed instance; footer is a disabled label
//
// MR 5 ships only `workspace`. Preview + read-only modes join in later
// MRs (preview is wired into the Preview page in MR 6 polish; read-only
// after Completion ships in MR 10).
//
// 560px right drawer per the handoff spec. Uses the design-system Drawer.

import { useState } from 'react';
import { Drawer } from '@gc-coach/design-system';
import type {
  Activity,
  ActivityState,
  ActivityStateEntry,
  Phase,
} from '@gc-coach/shared';
import { useI18n } from '../i18n/index.js';
import styles from './ActivityDrawer.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
  phase: Phase | null;
  phaseIndex: number;
  activity: Activity | null;
  state: ActivityStateEntry | null;
  /** Activities this one depends on — resolved titles. */
  dependencyTitles: ReadonlyArray<string>;
  /** True when any dependency is not yet complete. "Blocked" is a condition,
   *  not a state (handoff rule). */
  blocked: boolean;
  /** Called when the user transitions the activity state. */
  onTransition: (next: ActivityState) => void;
  /** Called when the user adds a note. */
  onAddNote: (note: string) => void;
  /** True while a mutation is in flight — buttons go into pending look. */
  pending: boolean;
  /** Opens a Coach session with this framework attached. V1 prefills no
   *  activity context; a future MR will pass the activity id so the first
   *  user message is seeded with "I'm working on {activity.title}". */
  onAskCoach?: () => void;
  /** Mirrors `pending` for the Ask-the-Coach button so the user can see
   *  the request is in flight. */
  askingCoach?: boolean;
}

export function ActivityDrawer(props: Props) {
  const { t } = useI18n();
  const [noteDraft, setNoteDraft] = useState('');

  const { phase, phaseIndex, activity, state } = props;
  if (!phase || !activity || !state) return null;

  const duration = activity.duration?.default_months
    ? t(`frameworks.duration.${activity.duration.default_months}`)
    : t('frameworks.preview.timeUnknown');

  const referenceDocs = (activity.documents ?? []).filter((d) => d.role === 'reference');
  const templateDocs = (activity.documents ?? []).filter(
    (d) => d.role === 'deliverable_template',
  );

  const stateLabel = t(`frameworks.workspace.state.${state.state}`);

  const canSubmitNote = noteDraft.trim().length > 0 && !props.pending;

  return (
    <Drawer
      open={props.open}
      onClose={props.onClose}
      width={560}
      title={
        <div>
          <div className={styles.headerEyebrow}>
            {t('frameworks.preview.phaseLabel')} {phaseIndex}
            <span className={`${styles.statePill} ${styles[`statePill_${state.state}`] ?? ''}`}>
              {stateLabel}
            </span>
            {props.blocked && (
              <span className={styles.blockedPill}>
                {t('frameworks.workspace.waiting')}
              </span>
            )}
          </div>
          <div className={styles.headerTitle}>{activity.title}</div>
        </div>
      }
      footer={
        <div className={styles.footerRow}>
          {state.state === 'not_started' && (
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={() => props.onTransition('in_progress')}
              disabled={props.pending}
            >
              {t('frameworks.workspace.markStarted')}
            </button>
          )}
          {state.state === 'in_progress' && (
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={() => props.onTransition('complete')}
              disabled={props.pending}
            >
              ✓ {t('frameworks.workspace.markComplete')}
            </button>
          )}
          {state.state === 'awaiting_review' && (
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={() => props.onTransition('complete')}
              disabled={props.pending}
            >
              ✓ {t('frameworks.workspace.markComplete')}
            </button>
          )}
          {state.state === 'complete' && (
            <button
              type="button"
              className={styles.ghostBtn}
              onClick={() => props.onTransition('in_progress')}
              disabled={props.pending}
            >
              {t('frameworks.workspace.reopen')}
            </button>
          )}
          <span style={{ flex: 1 }} />
          {props.onAskCoach && (
            <button
              type="button"
              className={styles.ghostBtn}
              onClick={props.onAskCoach}
              disabled={props.askingCoach}
            >
              ✦{' '}
              {props.askingCoach
                ? t('frameworks.workspace.askCoachOpening')
                : t('frameworks.workspace.askCoachAbout')}
            </button>
          )}
          <button type="button" className={styles.ghostBtn} onClick={props.onClose}>
            {t('frameworks.workspace.closeDrawer')}
          </button>
        </div>
      }
    >
      {activity.description && (
        <p className={styles.description}>{activity.description}</p>
      )}

      {props.blocked && (
        <div className={styles.blockedBlock}>
          {t('frameworks.workspace.blockedBlockPrefix')}{' '}
          <strong>{props.dependencyTitles.join(', ')}</strong>
          {'. '}
          {t('frameworks.workspace.blockedBlockSuffix')}
        </div>
      )}

      <div className={styles.kv}>
        <span className={styles.k}>{t('frameworks.workspace.durationLabel')}</span>
        <span className={styles.v}>{duration}</span>
      </div>
      <div className={styles.kv}>
        <span className={styles.k}>{t('frameworks.workspace.dependsOnLabel')}</span>
        <span className={styles.v}>
          {props.dependencyTitles.length === 0
            ? t('frameworks.workspace.dependsOnNothing')
            : props.dependencyTitles.join(', ')}
        </span>
      </div>
      {activity.stakeholders && activity.stakeholders.length > 0 && (
        <div className={styles.kv}>
          <span className={styles.k}>{t('frameworks.workspace.stakeholdersLabel')}</span>
          <span className={styles.v}>
            <span className={styles.stakeChips}>
              {activity.stakeholders.map((s) => (
                <span key={s} className={styles.stakeChip}>
                  {s}
                </span>
              ))}
            </span>
          </span>
        </div>
      )}

      {referenceDocs.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionLabel}>
            {t('frameworks.workspace.referenceLabel')}
          </div>
          <div className={styles.docList}>
            {referenceDocs.map((d, i) => (
              <div key={`${d.source}:${d.title}:${i}`} className={styles.docRow}>
                {d.title || describeDoc(d)}
              </div>
            ))}
          </div>
        </div>
      )}

      {templateDocs.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionLabel}>
            {t('frameworks.workspace.producesDeliverableLabel')}
          </div>
          <div className={styles.templateList}>
            {templateDocs.map((d, i) => (
              <div key={`${d.source}:${d.title}:${i}`} className={styles.templateRow}>
                {d.title || describeDoc(d)}
              </div>
            ))}
          </div>
          <div className={styles.sectionNote}>
            {t('frameworks.workspace.templateNote')}
          </div>
        </div>
      )}

      {/* Activity log + note composer */}
      <div className={styles.section}>
        <div className={styles.sectionLabel}>{t('frameworks.workspace.logLabel')}</div>
        {state.notes.length === 0 ? (
          <div className={styles.sectionNote}>{t('frameworks.workspace.logEmpty')}</div>
        ) : (
          <ul className={styles.logList}>
            {state.notes.map((n, i) => (
              <li key={i} className={styles.logEntry}>
                <div className={styles.logMeta}>
                  <strong>{n.author}</strong> ·{' '}
                  {new Date(n.created_at).toLocaleString()}
                </div>
                <div className={styles.logText}>{n.text}</div>
              </li>
            ))}
          </ul>
        )}
        <textarea
          className={styles.noteInput}
          placeholder={t('frameworks.workspace.addNotePlaceholder')}
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.currentTarget.value)}
          rows={2}
          disabled={props.pending}
        />
        <div className={styles.noteBtnRow}>
          <button
            type="button"
            className={styles.ghostBtn}
            disabled={!canSubmitNote}
            onClick={() => {
              props.onAddNote(noteDraft.trim());
              setNoteDraft('');
            }}
          >
            {t('frameworks.workspace.addNoteSubmit')}
          </button>
        </div>
      </div>
    </Drawer>
  );
}

type DocLike = { source: string; kb_entry_id?: string; uploaded_file_ref?: string };
function describeDoc(d: DocLike): string {
  if (d.source === 'uploaded_file' && d.uploaded_file_ref) return d.uploaded_file_ref;
  if (d.source === 'kb_entry_ref' && d.kb_entry_id) return d.kb_entry_id;
  return d.source;
}
